import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import StickyCallBar from "./StickyCallBar";
import ThemeToggle from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { useCompanyInfo } from "@/context/CompanyInfoContext";
import { useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/fleet", label: "Fleet" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { info } = useCompanyInfo();
  const { theme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--lt-bg)] text-[color:var(--lt-text)] flex flex-col">
      <header data-testid="main-header-nav" className="sticky top-0 z-50 bg-[var(--lt-panel)] backdrop-blur-md border-b border-[var(--lt-border)] shadow-[0_1px_0_var(--lt-shadow)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo-link" className="shrink-0"><Logo size="md" /></Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-[color:var(--lt-accent)] after:content-[''] after:absolute after:-bottom-2 after:left-0 after:right-0 after:h-[3px] after:bg-[color:var(--lt-accent)] after:rounded-full"
                      : "text-[color:var(--lt-navy)] hover:text-[color:var(--lt-accent)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle testId="theme-toggle-desktop" />
            <Button
              onClick={() => navigate("/quote")}
              data-testid="header-quote-btn"
              className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white font-semibold rounded-full h-12 pl-4 pr-6 shadow-[0_8px_20px_-8px_var(--lt-accent)]"
            >
              <Phone className="h-4 w-4 mr-2" /> Get a Quote
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle testId="theme-toggle-mobile" />
            <button
              className="text-[color:var(--lt-navy)]"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--lt-border)] bg-[var(--lt-panel)]" data-testid="mobile-nav">
            <div className="px-4 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className="text-sm font-semibold text-[color:var(--lt-navy)] py-1"
                >
                  {n.label}
                </NavLink>
              ))}
              <Button
                onClick={() => { setOpen(false); navigate("/quote"); }}
                data-testid="mobile-quote-btn"
                className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-full mt-2"
              >
                Get a Quote
              </Button>
            </div>
          </div>
        )}
      </header>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-[var(--lt-accent)] focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-head focus:text-xs focus:uppercase focus:tracking-widest"
      >
        Skip to main content
      </a>
      <main id="main-content" className="flex-1 page-enter" tabIndex={-1}>
        <Outlet context={useMemo(() => ({ info }), [info])} />
      </main>

      <footer data-lt-scheme="dark" className="bg-[color:var(--lt-navy-deep)] text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-white px-3 py-2 rounded-md" data-lt-scheme="light"><Logo size="sm" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm text-slate-300">
            <span>{info?.address_line || "Hoffman Estates, IL"}</span>
            {info?.mc_number && <span>MC# {info.mc_number}</span>}
            {info?.dot_number && <span>DOT# {info.dot_number}</span>}
            <a href={`https://${(info?.email || "lucastransinc.com").split("@").pop()}`} className="text-[color:var(--lt-accent-bright)] hover:text-white">
              lucastransinc.com
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row md:items-center justify-between gap-2 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} Lucas Trans Inc. All rights reserved.</div>
            <div className="flex flex-wrap gap-4">
              <Link to="/careers" className="hover:text-white">Careers</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</Link>
              <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
              <Link to="/driver-consent" className="hover:text-white" data-testid="footer-driver-consent">Driver Consent</Link>
              <Link to="/login" className="hover:text-white">Dispatch Login</Link>
            </div>
          </div>
        </div>
      </footer>
      <Toaster theme={theme} />
      <StickyCallBar />
    </div>
  );
}
